// src/pages/fiscal/mdfe/MDFeEmissaoPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaFileInvoice, FaPlus, FaRoute, FaSearch, FaShieldAlt, FaTruck, FaTrash } from 'react-icons/fa';
import { MdSend } from 'react-icons/md';
import Input from '../../../components/input/Input';
import InputCheck from '../../../components/input/InputCheck';
import Select from '../../../components/input/Select';
import PageContainer from '../../../components/geral/PageContainer';
import CidadeSearch from '../../geral/CidadeSearch';
import { useMessage } from '../../../contexts/geral/MessageContext';
import { useMDFe } from '../../../contexts/fiscal/MDFeContext';
import { useTransporteFiscal } from '../../../contexts/fiscal/TransporteFiscalContext';
import { MDFe, UFS, getSituacaoMDFe } from '../../../models/fiscal/MDFe';
import NFeAutorizadaSearch from './NFeAutorizadaSearch';
import './MDFe.css';

const ufOptions = [{ value: '', label: 'Selecione' }, ...UFS.map((uf) => ({ value: uf, label: uf }))];
const moeda = (valor) => Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const codigoCidade = (cidade = {}) => Number(cidade.cMun ?? cidade.CMun ?? cidade.codigoMunicipio ?? cidade.CodigoMunicipio ?? cidade.id ?? cidade.Id ?? 0);
const nomeCidade = (cidade = {}) => cidade.nome ?? cidade.Nome ?? cidade.municipioDescarga ?? cidade.MunicipioDescarga ?? '';
const ufCidade = (cidade = {}) => (cidade.uf ?? cidade.UF ?? cidade.ufDescarga ?? cidade.UFDescarga ?? '').toString().trim().toUpperCase();
const obterCidadeDestinatario = (nota = {}) => nota.cidadeDescarga
  ?? nota.CidadeDescarga
  ?? nota.cliente?.pessoa?.endereco?.cidade
  ?? nota.Cliente?.Pessoa?.Endereco?.Cidade
  ?? nota;
const obterDescargaNota = (nota) => {
  const cidade = obterCidadeDestinatario(nota);
  return {
    codigoMunicipioDescarga: codigoCidade(cidade),
    municipioDescarga: nomeCidade(cidade),
    ufDescarga: ufCidade(cidade),
  };
};
const obterVeiculoTracaoId = (mdfe = {}) =>
  mdfe.veiculoTracaoId ?? mdfe.veiculos?.find((veiculo) => Number(veiculo.tipo) === 1)?.veiculoId ?? '';
const obterReboqueIds = (mdfe = {}) =>
  mdfe.reboqueIds ?? mdfe.veiculos?.filter((veiculo) => Number(veiculo.tipo) === 2).map((veiculo) => veiculo.veiculoId).filter(Boolean) ?? [];
const montarFormEdicao = (mdfe) => new MDFe({
  ...mdfe,
  ufInicio: mdfe.ufInicio ?? mdfe.UFInicio ?? '',
  ufFim: mdfe.ufFim ?? mdfe.UFFim ?? '',
  municipiosCarregamento: mdfe.municipiosCarregamento ?? [],
  ufsPercurso: mdfe.ufsPercurso ?? mdfe.percursos?.sort((a, b) => Number(a.ordem) - Number(b.ordem)).map((item) => item.uf) ?? [],
  veiculoTracaoId: obterVeiculoTracaoId(mdfe),
  reboqueIds: obterReboqueIds(mdfe),
  condutorIds: mdfe.condutorIds ?? mdfe.condutores?.map((condutor) => condutor.condutorId).filter(Boolean) ?? [],
  notas: (mdfe.notas ?? []).map((nota) => ({
    notaId: nota.notaId,
    codigoMunicipioDescarga: nota.codigoMunicipioDescarga,
    municipioDescarga: nota.municipioDescarga,
    ufDescarga: nota.ufDescarga,
    nota: { chave: nota.chave, vNota: nota.valor },
  })),
  seguros: (mdfe.seguros ?? []).map((seguro) => ({
    seguradoraId: seguro.seguradoraId ?? '',
    responsavel: seguro.responsavel ?? 1,
    numeroApolice: seguro.numeroApolice ?? '',
    averbacoes: seguro.averbacoes?.length ? [...seguro.averbacoes] : [''],
  })),
});

const MDFeEmissaoPage = () => {
  const [searchParams] = useSearchParams();
  const { showMessage } = useMessage();
  const mdfeApi = useMDFe();
  const transporte = useTransporteFiscal();
  const [form, setForm] = useState(new MDFe({ filialId: 1 }));
  const [rascunho, setRascunho] = useState(null);
  const [notasDisponiveis, setNotasDisponiveis] = useState([]);
  const [cidadeMode, setCidadeMode] = useState(null);
  const [seguroAtivo, setSeguroAtivo] = useState(false);
  const [notaSearch, setNotaSearch] = useState(false);
  const mdfeId = Number(searchParams.get('id') || 0);

  useEffect(() => {
    const carregarRascunho = async () => {
      if (!mdfeId) {
        setForm(new MDFe({ filialId: 1 }));
        setRascunho(null);
        return;
      }

      const result = await mdfeApi.obter(mdfeId);
      if (!result) return;
      setForm(montarFormEdicao(result));
      setSeguroAtivo((result.seguros ?? []).length > 0);
      setRascunho(result);
    };

    carregarRascunho();
    mdfeApi.buscarNotas(1).then(setNotasDisponiveis).catch(() => setNotasDisponiveis([]));
    ['veiculos', 'condutores', 'seguradoras'].forEach((tipo) => transporte.carregar(tipo, 1));
  }, [mdfeId]);

  const alterar = (campo, valor) => setForm((prev) => Object.assign(new MDFe(prev), { [campo]: valor }));
  const ativos = (lista) => lista.filter((item) => item.ativo && item.filialId === 1);
  const valorCarga = useMemo(() => form.notas.reduce((total, item) => total + Number(item.nota?.vNota || 0), 0), [form.notas]);
  const notasPorMunicipio = useMemo(() => Object.values(form.notas.reduce((grupos, item) => {
    const chave = `${item.codigoMunicipioDescarga}-${item.municipioDescarga}-${item.ufDescarga}`;
    if (!grupos[chave]) {
      grupos[chave] = {
        codigoMunicipioDescarga: item.codigoMunicipioDescarga,
        municipioDescarga: item.municipioDescarga,
        ufDescarga: item.ufDescarga,
        notas: [],
      };
    }
    grupos[chave].notas.push(item);
    return grupos;
  }, {})), [form.notas]);

  const adicionarNota = (nota) => {
    if (form.notas.some((item) => item.notaId === nota.id)) return;

    const descarga = obterDescargaNota(nota);
    if (!descarga.codigoMunicipioDescarga || !descarga.municipioDescarga || descarga.ufDescarga.length !== 2) {
      showMessage('A NF-e selecionada não possui município de entrega/destinatário com código IBGE e UF.', 'warning');
      return;
    }

    alterar('notas', [...form.notas, { notaId: nota.id, ...descarga, nota }]);
  };

  const selecionarCidade = (cidade) => {
    const codigoMunicipio = codigoCidade(cidade);
    const nomeMunicipio = nomeCidade(cidade);

    if (!codigoMunicipio || !nomeMunicipio) {
      showMessage('Selecione uma cidade com código IBGE válido.', 'warning');
      setCidadeMode(null);
      return;
    }

    if (!form.municipiosCarregamento.some((item) => Number(item.codigoMunicipio) === codigoMunicipio)) {
      alterar('municipiosCarregamento', [...form.municipiosCarregamento, { codigoMunicipio, nomeMunicipio }]);
    }
    setCidadeMode(null);
  };

  const adicionarSeguro = () => alterar('seguros', [...form.seguros, {
    seguradoraId: '', responsavel: 1, numeroApolice: '', averbacoes: [''],
  }]);

  const salvar = async () => {
    const model = new MDFe(form);
    if (!seguroAtivo) model.seguros = [];
    const erros = model.validar();
    if (erros.length) return showMessage(erros.join('\n'), 'warning');
    const result = rascunho?.id
      ? await mdfeApi.atualizar(rascunho.id, model.toPayload())
      : await mdfeApi.criar(model.toPayload());
    if (!result) return;
    setRascunho(result);
  };

  const emitir = async () => {
    if (!rascunho?.id) return showMessage('Salve o rascunho antes de emitir.', 'warning');
    const result = await mdfeApi.emitir(rascunho.id);
    if (!result) return;
    setRascunho(result);
    showMessage(`${getSituacaoMDFe(result.situacao).label}\n${result.codigoStatus ?? ''} ${result.motivoStatus ?? ''}`,
      result.situacao === 3 ? 'danger' : 'info');
  };

  return (
    <PageContainer>
      <div className="mdfe-page">
        <header className="mdfe-titlebar">
          <div><h2><FaFileInvoice /> Emissão de MDF-e</h2><span>Modelo 58 · modal rodoviário · carga própria</span></div>
          {rascunho && <span className={`badge bg-${getSituacaoMDFe(rascunho.situacao).className}`}>{getSituacaoMDFe(rascunho.situacao).label}</span>}
        </header>

        <section className="mdfe-card">
          <h3><FaRoute /> Dados da viagem</h3>
          <div className="mdfe-grid">
            <Select label="UF inicial" value={form.ufInicio} options={ufOptions} onChange={(e) => alterar('ufInicio', e.target.value)} size="100%" />
            <Select label="UF final" value={form.ufFim} options={ufOptions} onChange={(e) => alterar('ufFim', e.target.value)} size="100%" />
            <Input label="Início previsto" type="datetime-local" value={form.dataInicioViagem} onChange={(e) => alterar('dataInicioViagem', e.target.value)} size="100%" />
          </div>
          <div className="mdfe-collection-head"><strong>Municípios de carregamento</strong><button className="btn btn-sm btn-outline-primary" onClick={() => setCidadeMode('carregamento')}><FaPlus /> Município</button></div>
          <div className="mdfe-chips">{form.municipiosCarregamento.map((cidade, index) => <span key={`${cidade.codigoMunicipio}-${index}`}>{cidade.nomeMunicipio}<button onClick={() => alterar('municipiosCarregamento', form.municipiosCarregamento.filter((_, i) => i !== index))}>×</button></span>)}</div>
          <div className="mdfe-collection-head"><strong>UFs de percurso (na ordem)</strong></div>
          <div className="d-flex gap-2 flex-wrap">
            <Select label="Adicionar UF" value="" size={150} options={ufOptions.filter((item) => !form.ufsPercurso.includes(item.value) && ![form.ufInicio, form.ufFim].includes(item.value))} onChange={(e) => e.target.value && alterar('ufsPercurso', [...form.ufsPercurso, e.target.value])} />
            <div className="mdfe-chips">{form.ufsPercurso.map((uf, index) => <span key={uf}>{index + 1}. {uf}<button onClick={() => alterar('ufsPercurso', form.ufsPercurso.filter((item) => item !== uf))}>×</button></span>)}</div>
          </div>
        </section>

        <section className="mdfe-card">
          <h3><FaFileInvoice /> Documentos vinculados</h3>
          <div className="d-flex gap-2 align-items-end flex-wrap">
            <button type="button" className="btn btn-outline-primary" onClick={() => setNotaSearch(true)}><FaSearch /> Buscar NF-e autorizada</button>
          </div>
          <div className="mdfe-table-wrap"><table className="mdfe-table"><thead><tr><th>Série/Número</th><th>Chave</th><th>Destinatário</th><th>Valor</th><th /></tr></thead>
            <tbody>
              {notasPorMunicipio.map((grupo) => (
                <React.Fragment key={`${grupo.codigoMunicipioDescarga}-${grupo.municipioDescarga}-${grupo.ufDescarga}`}>
                  <tr><td colSpan="5"><strong>{grupo.municipioDescarga}/{grupo.ufDescarga}</strong> · {grupo.codigoMunicipioDescarga} · {grupo.notas.length} NF-e</td></tr>
                  {grupo.notas.map((item) => <tr key={item.notaId}><td>{item.nota?.serie}/{item.nota?.numDoc}</td><td className="mdfe-key">{item.nota?.chave}</td><td>{item.nota?.clienteNome}</td><td>{moeda(item.nota?.vNota)}</td><td><button className="btn btn-sm btn-outline-danger" onClick={() => alterar('notas', form.notas.filter((nota) => nota.notaId !== item.notaId))}><FaTrash /></button></td></tr>)}
                </React.Fragment>
              ))}
              {!form.notas.length && <tr><td colSpan="5" className="text-center">Nenhuma NF-e vinculada.</td></tr>}
            </tbody>
          </table></div>
        </section>

        <section className="mdfe-card"><h3><FaTruck /> Carga e transporte</h3>
          <div className="mdfe-grid">
            <Input label="Peso/quantidade" type="number" value={form.quantidadeCarga} onChange={(e) => alterar('quantidadeCarga', e.target.value)} size="100%" />
            <Select label="Unidade" value={form.unidadeCarga} onChange={(e) => alterar('unidadeCarga', Number(e.target.value))} options={[{ value: 1, label: 'KG' }, { value: 2, label: 'TON' }]} size="100%" />
            <Input label="Valor total das NF-e" value={moeda(valorCarga)} disabled size="100%" />
            <Input label="Produto predominante" value={form.produtoPredominante} onChange={(e) => alterar('produtoPredominante', e.target.value)} size="100%" />
            <Input label="NCM predominante" value={form.ncmProdutoPredominante} onChange={(e) => alterar('ncmProdutoPredominante', e.target.value)} size="100%" />
            <Select label="Veículo de tração" value={form.veiculoTracaoId} onChange={(e) => alterar('veiculoTracaoId', e.target.value)} size="100%" options={[{ value: '', label: 'Selecione' }, ...ativos(transporte.veiculos).filter((v) => v.tipo === 1).map((v) => ({ value: v.id, label: `${v.placa} · ${v.renavam || 'sem RENAVAM'}` }))]} />
          </div>
          <div className="mdfe-check-list"><strong>Reboques</strong>{ativos(transporte.veiculos).filter((v) => v.tipo === 2).map((v) => <InputCheck key={v.id} id={`reboque-${v.id}`} label={v.placa} checked={form.reboqueIds.includes(v.id)} onChange={(e) => alterar('reboqueIds', e.target.checked ? [...form.reboqueIds, v.id] : form.reboqueIds.filter((id) => id !== v.id))} />)}<button className="btn btn-sm btn-link" onClick={async () => { await transporte.showVeiculoCad(); transporte.carregar('veiculos', 1); }}>Cadastrar veículo</button></div>
          <div className="mdfe-check-list"><strong>Condutores</strong>{ativos(transporte.condutores).map((c) => <InputCheck key={c.id} id={`condutor-${c.id}`} label={`${c.nome} · ${c.cpf}`} checked={form.condutorIds.includes(c.id)} onChange={(e) => alterar('condutorIds', e.target.checked ? [...form.condutorIds, c.id] : form.condutorIds.filter((id) => id !== c.id))} />)}<button className="btn btn-sm btn-link" onClick={async () => { await transporte.showCondutorCad(); transporte.carregar('condutores', 1); }}>Cadastrar condutor</button></div>
          <div className="mdfe-grid mdfe-textareas"><Input label="Informações do contribuinte" rows={3} value={form.informacaoContribuinte} onChange={(e) => alterar('informacaoContribuinte', e.target.value)} size="100%" /><Input label="Informações do Fisco" rows={3} value={form.informacaoFisco} onChange={(e) => alterar('informacaoFisco', e.target.value)} size="100%" /></div>
        </section>

        <section className="mdfe-card"><h3><FaShieldAlt /> Seguro</h3>
          <InputCheck id="seguro-ativo" label="Informar seguro" checked={seguroAtivo} onChange={(e) => { setSeguroAtivo(e.target.checked); if (e.target.checked && !form.seguros.length) adicionarSeguro(); }} />
          {seguroAtivo && form.seguros.map((seguro, index) => <div className="mdfe-insurance" key={index}>
            <Select label="Seguradora" value={seguro.seguradoraId} size={250} options={[{ value: '', label: 'Selecione' }, ...ativos(transporte.seguradoras).map((s) => ({ value: s.id, label: s.nome }))]} onChange={(e) => alterar('seguros', form.seguros.map((s, i) => i === index ? { ...s, seguradoraId: e.target.value } : s))} />
            <Select label="Responsável" value={seguro.responsavel} size={160} options={[{ value: 1, label: 'Emitente' }, { value: 2, label: 'Contratante' }]} onChange={(e) => alterar('seguros', form.seguros.map((s, i) => i === index ? { ...s, responsavel: Number(e.target.value) } : s))} />
            <Input label="Apólice" value={seguro.numeroApolice} size={180} onChange={(e) => alterar('seguros', form.seguros.map((s, i) => i === index ? { ...s, numeroApolice: e.target.value } : s))} />
            {seguro.averbacoes.map((averbacao, avIndex) => <Input key={avIndex} label={`Averbação ${avIndex + 1}`} value={averbacao} size={180} onChange={(e) => alterar('seguros', form.seguros.map((s, i) => i === index ? { ...s, averbacoes: s.averbacoes.map((a, ai) => ai === avIndex ? e.target.value : a) } : s))} />)}
            <button className="btn btn-sm btn-outline-primary" onClick={() => alterar('seguros', form.seguros.map((s, i) => i === index ? { ...s, averbacoes: [...s.averbacoes, ''] } : s))}><FaPlus /> Averbação</button>
            <button className="btn btn-sm btn-link" onClick={async () => { await transporte.showSeguradoraCad(); transporte.carregar('seguradoras', 1); }}>Cadastrar seguradora</button>
          </div>)}
        </section>

        {rascunho?.motivoStatus && <div className={`alert alert-${rascunho.situacao === 3 ? 'danger' : 'info'}`}><strong>cStat {rascunho.codigoStatus}:</strong> {rascunho.motivoStatus}</div>}
        <div className="mdfe-actions"><button className="btn btn-outline-primary" disabled={mdfeApi.loading || (rascunho?.id && ![0, 3].includes(rascunho?.situacao))} onClick={salvar}>{rascunho?.id ? 'Atualizar rascunho' : 'Salvar rascunho'}</button><button className="btn btn-success" disabled={mdfeApi.loading || !rascunho?.id || ![0, 3].includes(rascunho?.situacao)} onClick={emitir}><MdSend /> Emitir MDF-e</button></div>
      </div>
      <NFeAutorizadaSearch
        show={notaSearch}
        onHide={() => setNotaSearch(false)}
        notas={notasDisponiveis.filter((nota) => !form.notas.some((item) => item.notaId === nota.id))}
        onSelect={(nota) => {
          adicionarNota(nota);
          setNotaSearch(false);
        }}
      />
      <CidadeSearch show={!!cidadeMode} onHide={() => setCidadeMode(null)} onSelectCidade={selecionarCidade} />
    </PageContainer>
  );
};

export default MDFeEmissaoPage;
